import { t } from 'i18next';
import { observer } from 'mobx-react';
import isNil from 'lodash/isNil';

import { useMeasurement } from '@/hooks/useMeasurement';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { CompressorActivityStateEnum } from '@/constants/enums';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import { getValveRelativePosition } from '@/comp/MapBuilder/nodes/valve/ValveNodeWidget.utils';

import Slider from '../Slider/Slider';
import { getValveSliderPoints } from '../RealtimeData.utils';

const ValveSliders = observer(({ entity }: { entity: Entity }) => {
  const { tvalue, tunit } = useMeasurement();
  const {
    deviceType,
    connectivity,
    activity,
    valveMinSetpoint,
    valveMaxSetpoint,
    valveActivity,
    valveCurrentPosition,
    spillValveHpLowLimitSetpointBar,
    spillValveHpPressureBar,
    spillValveLpTargetSetpointBar,
    spillValveLpPressureBar,
  } = entity;

  const sliderPoints = getValveSliderPoints(valveMinSetpoint, valveMaxSetpoint);

  return (
    <>
      {deviceType === DeviceType.SpillValve ? (
        <>
          <div className="main-slider">
            <Slider
              showTierLock
              title={`${t('HP')} ${t('pressureSetpoints')}`}
              unit={t(tunit('Bar'))}
              min={tvalue(Number((spillValveHpLowLimitSetpointBar * 0.99)?.toFixed(2)), 'bar')}
              max={null}
              value={
                !isNil(spillValveHpPressureBar) ? tvalue(Number(spillValveHpPressureBar?.toFixed(2)), 'bar') : t('N/A')
              }
              points={[
                {
                  value: tvalue(Number(spillValveHpLowLimitSetpointBar?.toFixed(2)), 'bar'),
                  label: t('lowLimit'),
                },
              ]}
              connectivity={connectivity}
              highlightSlider={activity?.toLowerCase() === CompressorActivityStateEnum.Load}
              addSideGaps
              hasTarget
            />
          </div>
          <div className="main-slider">
            <Slider
              showTierLock
              title={`${t('LP')} ${t('pressureSetpoints')}`}
              unit={t(tunit('Bar'))}
              min={tvalue(Number((spillValveLpTargetSetpointBar * 0.99)?.toFixed(2)), 'bar')}
              max={null}
              value={
                !isNil(spillValveLpPressureBar) ? tvalue(Number(spillValveLpPressureBar?.toFixed(2)), 'bar') : t('N/A')
              }
              points={[
                {
                  value: tvalue(Number(spillValveLpTargetSetpointBar?.toFixed(2)), 'bar'),
                  label: t('targetSetpoint'),
                  isTarget: true,
                },
              ]}
              connectivity={connectivity}
              highlightSlider={activity?.toLowerCase() === CompressorActivityStateEnum.Load}
              addSideGaps
              hasTarget
            />
          </div>
        </>
      ) : (
        <div className="main-slider">
          <Slider
            showTierLock
            title={t('relative position')}
            unit={t(tunit('%'))}
            min={0}
            max={100}
            value={getValveRelativePosition(valveActivity, valveCurrentPosition, deviceType) ?? t('N/A')}
            points={sliderPoints}
            connectivity={connectivity}
            highlightSlider={activity?.toLowerCase() === CompressorActivityStateEnum.Load}
            addSideGaps
            hasTarget
          />
        </div>
      )}
    </>
  );
});

export default ValveSliders;
